import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon, DownloadSimpleIcon, PencilSimpleIcon, TrashIcon,
  FileArrowDownIcon, CodeIcon, FilePdfIcon, PaperclipIcon, TrashSimpleIcon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusPill from "@/components/StatusPill";
import VersionHistory from "@/components/VersionHistory";
import { useAuth } from "@/context/AuthContext";
import {
  getIngredient, deleteIngredient, exportUrls, uploadDoc, deleteDoc,
  downloadIngredientDoc, downloadExport,
} from "@/lib/api";

function Field({ label, value, mono = false }) {
  const v = value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0) ? "—" : value;
  return (
    <div className="p-4 border-b border-r border-[var(--border)] min-h-[76px]" data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="label-kicker">{label}</div>
      <div className={`mt-2 text-sm ${mono ? "font-mono" : ""} text-[var(--text)] break-words`}>{Array.isArray(v) ? v.join(", ") : String(v)}</div>
    </div>
  );
}

function Chips({ items }) {
  if (!items || items.length === 0) return <span className="text-[var(--text-muted)]">—</span>;
  return <div className="flex flex-wrap gap-1.5">{items.map((t, i) => <span key={i} className="chip">{t}</span>)}</div>;
}

export default function IngredientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ing, setIng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("identity");
  const { hasRole } = useAuth();

  const load = ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    getIngredient(id)
      .then(setIng)
      .catch(() => toast.error("Ingredient not found"))
      .finally(() => { if (initial) setLoading(false); });
  };
  useEffect(() => { load({ initial: true }); /* eslint-disable-next-line */ }, [id]);

  const urls = exportUrls(id);

  const handleDelete = async () => {
    await deleteIngredient(id);
    toast.success("Ingredient deleted");
    navigate("/");
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadDoc(id, file);
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeDoc = async (docId) => {
    await deleteDoc(id, docId);
    toast.success("Document removed");
    load();
  };

  if (loading) return <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10"><div className="font-mono text-xs text-[var(--text-muted)]">Loading…</div></main>;
  if (!ing) return null;

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 fade-in" data-testid="detail-page">
      {/* Breadcrumb + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <Link to="/" className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="back-link">
          <ArrowLeftIcon size={14} /> Directory
        </Link>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="export-menu-trigger">
                <DownloadSimpleIcon size={14} className="mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-none">
              <DropdownMenuItem onClick={() => downloadExport(urls.json, `${ing.ingredientName}.json`)} data-testid="export-json">
                <CodeIcon size={14} className="mr-2" /> JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadExport(urls.pdf, `${ing.ingredientName}.pdf`)} data-testid="export-pdf">
                <FilePdfIcon size={14} className="mr-2" /> PDF Summary
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => downloadExport(urls.cpsr, `${ing.ingredientName}_CPSR.json`)} data-testid="export-cpsr">
                <FileArrowDownIcon size={14} className="mr-2" /> CPSR Data Block
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {hasRole("editor") && (
            <Button variant="outline" className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" onClick={() => navigate(`/ingredient/${id}/edit`)} data-testid="edit-btn">
              <PencilSimpleIcon size={14} className="mr-2" /> Edit
            </Button>
          )}
          {hasRole("admin") && (
            <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--danger)] border-[var(--danger)] hover:bg-[var(--danger)]/5" data-testid="delete-btn">
                <TrashIcon size={14} className="mr-2" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this ingredient?</AlertDialogTitle>
                <AlertDialogDescription>This action is permanent and cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none" data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="rounded-none bg-[var(--danger)] hover:bg-[var(--danger)]/90" data-testid="delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}
        </div>
      </div>

      {/* Header */}
      <section className="border border-[var(--border)] p-6 md:p-8 bg-white" data-testid="detail-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-kicker">{ing.category || "Ingredient"}</div>
            <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter mt-2">{ing.ingredientName}</h1>
            <div className="mt-2 font-mono text-xs text-[var(--text-muted)]">
              INCI · {ing.inciEU || "—"} {ing.casNumber ? `· CAS ${ing.casNumber}` : ""}
            </div>
          </div>
          <StatusPill status={ing.status} prohibited={ing.prohibited} />
        </div>
      </section>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList className="rounded-none bg-transparent p-0 border-b border-[var(--border)] h-auto w-full justify-start gap-0 overflow-x-auto">
          {[
            ["identity", "Identity"], ["regulatory", "Regulatory"], ["toxicology", "Toxicology"],
            ["cosmetic", "Cosmetic Use"], ["docs", "Documentation"], ["meta", "Notes & Metadata"],
            ["history", "History"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:tab-active data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] border-b-2 border-transparent" data-testid={`tab-${v}`}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="identity" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <Field label="INCI (EU)" value={ing.inciEU} mono />
            <Field label="INCI (US)" value={ing.inciUS} mono />
            <Field label="IUPAC Name" value={ing.iupacName} mono />
            <Field label="Chemical Name" value={ing.chemicalName} mono />
            <Field label="CAS Number" value={ing.casNumber} mono />
            <Field label="EINECS" value={ing.einecs} mono />
            <Field label="Alt Name" value={ing.altName} />
            <Field label="Category" value={ing.category} />
            <Field label="Source" value={ing.source} />
            <Field label="Country of Origin" value={ing.originCountry} />
          </div>
        </TabsContent>

        <TabsContent value="regulatory" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <Field label="Region" value={ing.region} />
            <Field label="Status" value={ing.prohibited ? "PROHIBITED" : ing.status} />
            <Field label="Area / Restriction" value={ing.areaRestriction} />
            <Field label="Region (Use)" value={ing.regionUse} />
            <Field label="Status Region" value={ing.statusRegion} />
            <Field label="Final Region" value={ing.finalRegion} />
            <Field label="Usage Restriction" value={ing.usageRestriction} />
            <Field label="Important Date" value={ing.importantDate} mono />
            <Field label="Min %" value={ing.minPercent} mono />
            <Field label="Max %" value={ing.maxPercent} mono />
          </div>

          {ing.regions && ing.regions.length > 0 && (
            <div className="mt-6" data-testid="regions-display">
              <div className="label-kicker mb-3">Additional Regions</div>
              <div className="space-y-3">
                {ing.regions.map((r, i) => (
                  <div key={i} className="border border-[var(--border)] p-4 bg-white" data-testid={`region-view-${i}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="font-heading text-lg font-medium tracking-tight">{r.region}</div>
                      <StatusPill status={r.status} prohibited={r.prohibited} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2 font-mono text-xs">
                      {r.usageRestriction && <div><span className="label-kicker block">Usage</span>{r.usageRestriction}</div>}
                      {r.areaRestriction && <div><span className="label-kicker block">Area</span>{r.areaRestriction}</div>}
                      {r.importantDate && <div><span className="label-kicker block">Date</span>{r.importantDate}</div>}
                      {r.minPercent && <div><span className="label-kicker block">Min %</span>{r.minPercent}</div>}
                      {r.maxPercent && <div><span className="label-kicker block">Max %</span>{r.maxPercent}</div>}
                      {r.note && <div className="md:col-span-3"><span className="label-kicker block">Note</span>{r.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="toxicology" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <Field label="NOAEL" value={ing.noael} mono />
            <Field label="Absorption" value={ing.absorption} mono />
            <Field label="Toxicological Data" value={ing.toxicology} />
            <Field label="Caution" value={ing.caution} />
            <Field label="Warning" value={ing.warning} />
            <Field label="Heavy Metal Concern" value={ing.heavyMetalConcern} />
          </div>
        </TabsContent>

        <TabsContent value="cosmetic" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <div className="p-4 border-b border-r border-[var(--border)]">
              <div className="label-kicker">Function</div>
              <div className="mt-2"><Chips items={ing.function} /></div>
            </div>
            <div className="p-4 border-b border-r border-[var(--border)]">
              <div className="label-kicker">Claims</div>
              <div className="mt-2"><Chips items={ing.claims} /></div>
            </div>
            <Field label="Composition" value={ing.composition} />
            <Field label="Impurity" value={ing.impurity} />
            <Field label="Description" value={ing.description} />
          </div>
        </TabsContent>

        <TabsContent value="docs" className="mt-0">
          <div className="border border-[var(--border)] p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="label-kicker">Attached Documents</div>
                <div className="font-mono text-xs text-[var(--text-muted)] mt-1">PDF, DOCX, CSV, images — any dossier file</div>
              </div>
              <label className="inline-flex">
                <input type="file" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="upload-input" disabled={!hasRole("editor")} />
                {hasRole("editor") && (
                  <Button asChild className="btn-primary rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em] cursor-pointer" disabled={uploading}>
                    <span>{uploading ? "Uploading…" : "Upload File"}</span>
                  </Button>
                )}
              </label>
            </div>

            {(!ing.documents || ing.documents.length === 0) ? (
              <div className="border border-dashed border-[var(--border)] p-10 text-center" data-testid="docs-empty">
                <PaperclipIcon size={22} className="mx-auto text-[var(--text-muted)]" />
                <div className="mt-2 font-mono text-xs text-[var(--text-muted)]">No documents attached yet.</div>
              </div>
            ) : (
              <ul className="border border-[var(--border)]" data-testid="docs-list">
                {ing.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between px-4 py-3 cell-border last:border-b-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <PaperclipIcon size={14} className="text-[var(--text-muted)]" />
                      <button onClick={() => downloadIngredientDoc(id, d.id, d.filename)} className="font-mono text-sm truncate hover:text-[var(--primary)] text-left" data-testid={`doc-link-${d.id}`}>
                        {d.filename}
                      </button>
                      <span className="font-mono text-[10px] text-[var(--text-muted)]">{(d.size / 1024).toFixed(1)} KB</span>
                    </div>
                    {hasRole("editor") && (
                      <button onClick={() => removeDoc(d.id)} className="text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid={`doc-remove-${d.id}`}>
                        <TrashSimpleIcon size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
              <Field label="Country (Docs)" value={ing.countryDocs} />
              <Field label="Ingredient Docs (reference)" value={ing.ingredientDocs} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="meta" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[var(--border)] border-r-0 border-b-0">
            <div className="p-4 border-b border-r border-[var(--border)]">
              <div className="label-kicker">Ingredient Types</div>
              <div className="mt-2"><Chips items={ing.ingredientTypes} /></div>
            </div>
            <Field label="Select Type" value={ing.selectType} />
            <Field label="Description" value={ing.description} />
            <Field label="Notes" value={ing.notes} />
            <Field label="Version" value={`v${ing.version || 1}`} mono />
            <Field label="Last Updated" value={ing.updated_at ? new Date(ing.updated_at).toLocaleString() : null} mono />
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <VersionHistory ingredientId={id} onRestored={load} />
        </TabsContent>
      </Tabs>
    </main>
  );
}
